import { memo, useEffect, useRef, useState } from "react"
import "./GameToastNotification.css"

/* ─────────────────────────────────────────────────────────────────────────────
   Classify a raw message string into a visual variant + icon + label.
───────────────────────────────────────────────────────────────────────────── */
function classifyMessage(msg = "") {
    const m = msg.toLowerCase()

    if (m.includes("congratulation") || m.includes("you won the game"))
        return { variant: "win", iconKey: "trophy", label: "🏆 You Win!" }

    if (m.includes("won the trick") && !m.includes("waiting"))
        return { variant: "win", iconKey: "win", label: "Trick Won" }

    if (m.includes("won the hand"))
        return { variant: "win", iconKey: "win", label: "Hand Won" }

    if (m.includes("won the envido"))
        return { variant: "win", iconKey: "win", label: "Envido Won" }

    if (m.includes("trick draw") || m.includes("no winner"))
        return { variant: "neutral", iconKey: "draw", label: "Trick Draw" }

    if (m.includes("called truco") || m.includes("truco!"))
        return { variant: "bet", iconKey: "bet", label: "🃏 Truco!" }

    if (m.includes("re-truco") || m.includes("raised the bet"))
        return { variant: "bet", iconKey: "raise", label: "🃏 Re-Truco!" }

    if (m.includes("cuatro") || m.includes("raised to cuatro"))
        return { variant: "bet", iconKey: "raise", label: "🃏 Cuatro!" }

    if (m.includes("falta envido!"))
        return { variant: "bet", iconKey: "envido", label: "Falta Envido" }

    if (m.includes("real envido"))
        return { variant: "bet", iconKey: "envido", label: "Real Envido" }

    if (m.includes("envido"))
        return { variant: "bet", iconKey: "envido", label: "Envido!" }

    if (m.includes("accepted"))
        return { variant: "info", iconKey: "accept", label: "Accepted" }

    if (m.includes("declined") || m.includes("rejected"))
        return { variant: "neutral", iconKey: "fold", label: "Declined" }

    if (m.includes("left the table"))
        return { variant: "neutral", iconKey: "fold", label: "Player Left" }

    if (m.includes("it's your turn") || m.includes("you lead") || m.includes("your turn"))
        return { variant: "turn", iconKey: "turn", label: "Your Turn ⚔️" }

    if (m.includes("waiting for your opponent") || m.includes("waiting for"))
        return { variant: "neutral", iconKey: "wait", label: "Waiting…" }

    if (m.includes("you played"))
        return { variant: "info", iconKey: "card", label: "Card Played" }

    if (m.includes("played a"))
        return { variant: "info", iconKey: "card", label: "Card Played" }

    if (m.includes("dealt") || m.includes("new hand"))
        return { variant: "info", iconKey: "deal", label: "Cards Dealt" }

    if (m.includes("passed"))
        return { variant: "neutral", iconKey: "fold", label: "Passed" }

    return { variant: "info", iconKey: "info", label: "Game Info" }
}

/* ─────────────────────────────────────────────────────────────────────────────
   SVG icons
───────────────────────────────────────────────────────────────────────────── */
const ICONS = {
    turn: <svg viewBox="0 0 24 24"><path d="M12 2a5 5 0 1 1 0 10A5 5 0 0 1 12 2zm0 12c5.33 0 8 2.67 8 4v2H4v-2c0-1.33 2.67-4 8-4z" /></svg>,
    wait: <svg viewBox="0 0 24 24"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" /></svg>,
    bet: <svg viewBox="0 0 24 24"><path d="M7 14l5-5 5 5z" /></svg>,
    raise: <svg viewBox="0 0 24 24"><path d="M7 14l5-5 5 5zM7 10l5-5 5 5z" /></svg>,
    envido: <svg viewBox="0 0 24 24"><path d="M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" /></svg>,
    win: <svg viewBox="0 0 24 24"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" /></svg>,
    trophy: <svg viewBox="0 0 24 24"><path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z" /></svg>,
    draw: <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" /></svg>,
    accept: <svg viewBox="0 0 24 24"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" /></svg>,
    fold: <svg viewBox="0 0 24 24"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" /></svg>,
    card: <svg viewBox="0 0 24 24"><path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9H9V9h10v2zm-4 4H9v-2h6v2zm4-8H9V5h10v2z" /></svg>,
    deal: <svg viewBox="0 0 24 24"><path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9h-4v4h-2v-4H9V9h4V5h2v4h4v2z" /></svg>,
    info: <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" /></svg>,
}

/* ─────────────────────────────────────────────────────────────────────────────
   Position styles — fed from the settings dialog
───────────────────────────────────────────────────────────────────────────── */
export const POSITION_STYLES = {
    "top-left": { top: "76px", left: "16px", bottom: "auto", right: "auto", transform: "none", alignItems: "flex-start" },
    "top-center": { top: "76px", left: "50%", bottom: "auto", right: "auto", transform: "translateX(-50%)", alignItems: "center" },
    "top-right": { top: "76px", right: "16px", bottom: "auto", left: "auto", transform: "none", alignItems: "flex-end" },
    "center": { top: "55.5%", left: "50%", bottom: "auto", right: "auto", transform: "translate(-50%,-50%)", alignItems: "center" },
    "bottom-left": { bottom: "80px", left: "16px", top: "auto", right: "auto", transform: "none", alignItems: "flex-start" },
    "bottom-center": { bottom: "80px", left: "50%", top: "auto", right: "auto", transform: "translateX(-50%)", alignItems: "center" },
    "bottom-right": { bottom: "80px", right: "16px", top: "auto", left: "auto", transform: "none", alignItems: "flex-end" },
}

const DISPLAY_MS = 5000
const EXIT_MS = 420

/* ─────────────────────────────────────────────────────────────────────────────
   GameToastNotification
   Props:
     message      (string)  — from playerActionMessage in TableCanvas
     positionKey  (string)  — one of the POSITION_STYLES keys
───────────────────────────────────────────────────────────────────────────── */
function GameToastNotification({ message = "", positionKey = "center" }) {
    const [toasts, setToasts] = useState([])
    const counterRef = useRef(0)
    const timerMapRef = useRef({})   // id → { dismissTid, removeTid }
    const lastMsgRef = useRef("")

    // ── dismissToast is stable (no deps) — reads only refs and setToasts ──
    // Defined BEFORE the effect so it can be referenced inside it without
    // going stale.  Using useRef to hold the function avoids adding it as
    // an effect dependency.
    const dismissToastRef = useRef(null)
    dismissToastRef.current = (id) => {
        const entry = timerMapRef.current[id]
        if (!entry) return                         // already dismissed
        clearTimeout(entry.dismissTid)
        clearTimeout(entry.removeTid)
        delete timerMapRef.current[id]

        // Phase 1: trigger CSS exit animation
        setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t))

        // Phase 2: remove from DOM after animation completes
        const removeTid = setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id))
        }, EXIT_MS)

        // Store removeTid so we can cancel it if the component unmounts
        timerMapRef.current[`remove-${id}`] = removeTid
    }

    /* Fire a new toast whenever `message` changes (non-empty, non-repeat) */
    useEffect(() => {
        if (!message || message === lastMsgRef.current) return
        lastMsgRef.current = message

        const id = ++counterRef.current
        const { variant, iconKey, label } = classifyMessage(message)

        setToasts(prev => {
            // Evict oldest if already at cap
            const kept = prev.length >= 3 ? prev.slice(1) : prev
            return [...kept, { id, message, variant, iconKey, label, exiting: false }]
        })

        // Schedule auto-dismiss for THIS specific toast id.
        // Each toast gets its own independent entry in timerMapRef so they
        // never interfere with each other — this is the root cause fix for
        // "only the last toast clears automatically".
        const dismissTid = setTimeout(() => {
            dismissToastRef.current(id)
        }, DISPLAY_MS)

        timerMapRef.current[id] = { dismissTid, removeTid: null }

        // No cleanup needed here — we intentionally do NOT cancel the timer
        // on message change because we want previously-queued toasts to keep
        // their own independent timers running.  The timerMapRef is the source
        // of truth; dismissToast guards against double-calls via the entry check.
    }, [message]) // eslint-disable-line react-hooks/exhaustive-deps

    // Cancel ALL outstanding timers on unmount
    useEffect(() => {
        return () => {
            Object.values(timerMapRef.current).forEach(entry => {
                if (typeof entry === 'number') clearTimeout(entry)           // remove-N entries
                else if (entry?.dismissTid) clearTimeout(entry.dismissTid)  // dismiss entries
            })
            timerMapRef.current = {}
        }
    }, [])

    const dismissToast = (id) => dismissToastRef.current(id)

    const posStyle = POSITION_STYLES[positionKey] || POSITION_STYLES["bottom-center"]

    if (!toasts.length) return null

    return (
        <div className="gtn-container" style={posStyle}>
            {toasts.map(toast => (
                <div
                    key={toast.id}
                    className={`gtn-toast gtn-${toast.variant}${toast.exiting ? " gtn-out" : ""}`}
                    role="alert"
                    aria-live="polite"
                >
                    {/* Left accent bar */}
                    <div className="gtn-accent" />

                    {/* Icon badge */}
                    <div className={`gtn-icon gtn-icon-${toast.variant}`}>
                        {ICONS[toast.iconKey] || ICONS.info}
                    </div>

                    {/* Text */}
                    <div className="gtn-body">
                        <span className="gtn-label">{toast.label}</span>
                        <span className="gtn-message">{toast.message}</span>
                    </div>

                    {/* Close */}
                    <button
                        className="gtn-close"
                        aria-label="Dismiss"
                        onClick={() => dismissToast(toast.id)}
                    >✕</button>

                    {/* Countdown progress bar */}
                    <div
                        className="gtn-progress"
                        style={{ animationDuration: `${DISPLAY_MS}ms` }}
                    />

                    {/* Shimmer sweep */}
                    <div className="gtn-shimmer" />
                </div>
            ))}
        </div>
    )
}

export default memo(GameToastNotification)